import type { AppStore } from './app';
import type { ResultsRecord } from '../types/results';
import { makeAutoObservable } from 'mobx';

import { publishSentryMessage } from '../configs/sentry/sentry-utils';


function sortedResults(rows: ResultsRecord[]): ResultsRecord[] {
  return Array.from(rows).sort((a, b) => Number(a.position) - Number(b.position));
}

export class ResultsStore {
  fetching = false;
  data: ResultsRecord[] = [];

  constructor(
    private appStore: AppStore,
    private resultsLimit = Infinity,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get token() {
    if (!this.appStore.token) {
      throw new Error('Token is required');
    }

    return this.appStore.token;
  }

  get results(): ResultsRecord[] {
    const sorted = sortedResults(this.data);
    if (!sorted.length) {
      return sorted;
    }

    if (this.appStore.prioritizeMyTeam) {
      const myTeam = sorted.find((item) => item.name === this.appStore.gameData?.team_name);
      const myTeamPosition = myTeam?.position;

      if (!myTeam) {
        publishSentryMessage('My team does not exist in the list.');
      } else if ((myTeamPosition || 0) > this.resultsLimit) {
        return [...sorted.slice(0, this.resultsLimit), myTeam];
      }
    }

    return sorted;
  }

  init = () => {
    this.listenSocket();
    this.fetch();
  };

  fetch = async () => {
    this.setFetching(true);
    try {
      const response = await this.appStore.api.results(this.token);
      this.setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // TODO: push error notification, add retry
      console.log(error);
    } finally {
      this.setFetching(false);
    }
  };

  listenSocket = () => {
    const socket = this.appStore.socket;
    if (!socket) {
      return;
    }
    socket.on('updateResults', () => {
      void this.fetch();
    });

    socket.io.on('reconnect', this.fetch);
  };

  setData(data: ResultsRecord[]) {
    this.data = data;
  }

  setFetching(value: boolean) {
    this.fetching = value;
  }
}
